import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Coins,
  PlayCircle,
  RefreshCw,
  Save,
  Users,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

export default function CreditApplySettings() {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [summaryEmail, setSummaryEmail] = useState("");
  const [lookaheadDays, setLookaheadDays] = useState(7);
  const [pushAcuityNote, setPushAcuityNote] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/credit-apply/config");
      setCfg(r.data);
      setEnabled(!!r.data.enabled);
      setSummaryEmail(r.data.summary_email || "");
      setLookaheadDays(Number(r.data.lookahead_days || 7));
      setPushAcuityNote(r.data.push_acuity_note !== false);
    } catch (e) {
      // best-effort
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function save() {
    setSaving(true);
    try {
      const r = await api.post("/credit-apply/config", {
        enabled,
        summary_email: summaryEmail.trim() || null,
        lookahead_days: Number(lookaheadDays),
        push_acuity_note: pushAcuityNote,
      });
      setCfg(r.data);
      toast.success("Credit apply settings saved");
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  async function runNow(sendEmail, force = false) {
    setRunning(true);
    setLastRun(null);
    try {
      const r = await api.post(
        `/credit-apply/run-now?send_email=${sendEmail ? "true" : "false"}&force=${force ? "true" : "false"}`,
        null,
        { timeout: 180000 }
      );
      setLastRun(r.data);
      const {
        applied_new,
        still_unpaid,
        appointments_seen,
        email_sent_to,
        email_skipped_reason,
        email_error,
      } = r.data || {};
      toast.success(
        `Scanned ${appointments_seen} appts · ${applied_new} credits applied · ${still_unpaid} unpaid`,
        {
          description: sendEmail
            ? email_sent_to
              ? `Summary emailed to ${email_sent_to}`
              : email_error
              ? `Email failed: ${email_error}`
              : email_skipped_reason || "Email not sent"
            : "Email skipped",
        }
      );
      await refresh();
    } catch (e) {
      toast.error("Run failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRunning(false);
    }
  }

  const summary = lastRun || cfg?.last_run_summary;

  return (
    <Card
      className="p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none"
      data-testid="credit-apply-settings"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <Coins className="h-3.5 w-3.5" /> Daily prepaid-credit auto-apply
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-zinc-600">
            {enabled ? "Enabled" : "Disabled"}
          </span>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="credit-apply-enabled-switch"
          />
        </div>
      </div>

      <p className="mt-3 text-sm text-zinc-600 max-w-3xl leading-relaxed">
        Each night at <strong>1:15 AM Melbourne</strong>, scan the next{" "}
        <span className="font-mono-num">{lookaheadDays}</span> days of
        appointments. For any showing as “Owing”, look up the client’s
        certificates / packages / subscriptions in Acuity and attach a
        matching credit. Anything still unpaid is emailed to you so you can
        chase it up.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-zinc-700">Summary email recipient</Label>
          <Input
            value={summaryEmail}
            onChange={(e) => setSummaryEmail(e.target.value)}
            placeholder="manager@example.com (defaults to your daily-schedule manager)"
            className="mt-1.5 bg-white border-zinc-300 focus-visible:ring-zinc-950"
            data-testid="credit-apply-summary-email"
          />
          <p className="mt-1.5 text-xs text-zinc-500">
            Leave blank to reuse the manager email from “Daily schedule
            email”. Uses the same Resend API key.
          </p>
        </div>
        <div>
          <Label className="text-zinc-700">Lookahead (days)</Label>
          <Input
            type="number"
            min={1}
            max={30}
            value={lookaheadDays}
            onChange={(e) => setLookaheadDays(e.target.value)}
            className="mt-1.5 bg-white border-zinc-300 font-mono-num focus-visible:ring-zinc-950"
            data-testid="credit-apply-lookahead-days"
          />
          <p className="mt-1.5 text-xs text-zinc-500">Today + N days ahead.</p>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3">
        <div>
          <div className="text-xs font-semibold text-zinc-700">
            Append a note to each Acuity appointment we credit
          </div>
          <div className="text-2xs text-zinc-500 mt-0.5">
            Adds <code className="font-mono-num">[auto-applied credit XXXXX on YYYY-MM-DD]</code> to
            the appointment’s notes field so you can see it in Acuity.
          </div>
        </div>
        <Switch
          checked={pushAcuityNote}
          onCheckedChange={setPushAcuityNote}
          data-testid="credit-apply-push-note-switch"
        />
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="credit-apply-save-btn"
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
        >
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving…" : "Save settings"}
        </Button>
        <Button
          onClick={() => runNow(true)}
          disabled={running}
          variant="outline"
          data-testid="credit-apply-run-now-btn"
          className="border-zinc-300 hover:bg-zinc-100"
        >
          <PlayCircle className={`h-4 w-4 mr-1.5 ${running ? "animate-pulse" : ""}`} />
          {running ? "Running…" : "Run now & email summary"}
        </Button>
        <Button
          onClick={() => runNow(false)}
          disabled={running}
          variant="ghost"
          data-testid="credit-apply-dry-run-btn"
          className="text-zinc-600 hover:text-zinc-900"
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${running ? "animate-spin" : ""}`} />
          Run without sending email
        </Button>
        <Button
          onClick={() => runNow(true, true)}
          disabled={running}
          variant="ghost"
          data-testid="credit-apply-force-run-btn"
          className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
          title="Re-attempt applying credits to appointments that are still outstanding in Acuity, even if a previous override exists. Safe — Acuity rejects double-redemption. Sends summary email."
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${running ? "animate-spin" : ""}`} />
          Force re-process & email
        </Button>
      </div>

      {summary && (
        <div className="mt-6 rounded-md border border-zinc-200 bg-white p-4" data-testid="credit-apply-last-run">
          <div className="text-2xs uppercase tracking-[0.2em] text-zinc-500">
            Last run
            {cfg?.last_run_at_iso && (
              <span className="ml-2 font-mono-num text-zinc-400 normal-case tracking-normal">
                {fmtDateTime(cfg.last_run_at_iso)}
              </span>
            )}
          </div>
          {(summary.certs_fetch_error ||
            summary.orders_fetch_error ||
            (summary.certs_pulled === 0 && (summary.appointments_seen ?? 0) > 0)) && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
              data-testid="credit-apply-fetch-warning">
              <strong>⚠ Treat this run as INVALID — Acuity data fetch problem.</strong>
              {summary.certs_fetch_error && (
                <div className="mt-1">/certificates: {summary.certs_fetch_error}</div>
              )}
              {!summary.certs_fetch_error && summary.certs_pulled === 0 && (
                <div className="mt-1">/certificates returned an empty list. Re-run manually below.</div>
              )}
              {summary.orders_fetch_error && (
                <div className="mt-1">/orders: {summary.orders_fetch_error}</div>
              )}
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <Stat label="Scanned" value={summary.appointments_seen} />
            <Stat label="Applied (new)" value={summary.applied_new} />
            <Stat
              label="Applied (total)"
              value={summary.applied_total ?? "—"}
            />
            <Stat
              label="Still unpaid"
              value={summary.still_unpaid}
              warn={summary.still_unpaid > 0}
            />
          </div>
          {typeof summary.certs_pulled === "number" && (
            <div className="mt-2 text-2xs text-zinc-500 font-mono-num">
              certificates pulled from Acuity: <span className={summary.certs_pulled === 0 ? "text-red-700 font-semibold" : ""}>{summary.certs_pulled}</span>
            </div>
          )}
          {summary.window && (
            <div className="mt-3 text-xs text-zinc-500 font-mono-num">
              window: {summary.window.min} → {summary.window.max}
            </div>
          )}
          {lastRun?.unpaid_remaining?.length > 0 && (
            <details className="mt-3" data-testid="credit-apply-unpaid-details">
              <summary className="text-sm text-zinc-700 cursor-pointer hover:text-zinc-900">
                Show {lastRun.unpaid_remaining.length} unpaid appointment(s)
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-zinc-700 max-h-64 overflow-auto thin-scroll">
                {lastRun.unpaid_remaining.map((u) => (
                  <li
                    key={u.appointment_id}
                    className="flex items-baseline justify-between gap-3 border-b border-zinc-100 pb-1"
                  >
                    <span className="font-mono-num text-2xs text-zinc-500 shrink-0">
                      {(u.appointment_time || "").slice(0, 16).replace("T", " ")}
                    </span>
                    <span className="flex-1 truncate">
                      <span className="font-medium">{u.client_name}</span>{" "}
                      <span className="text-zinc-500">· {u.appointment_type_name}</span>
                    </span>
                    <span className="font-mono-num text-zinc-700 shrink-0">
                      ${Number(u.expected_value || 0).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <CreditShareLinksBlock />

      <ClientEmailAliasesBlock />

      <CreditApplyDiagnoseBlock />
    </Card>
  );
}


function ClientEmailAliasesBlock() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, emails: string, label: string}

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/credit-apply/email-aliases");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error("Could not load email aliases", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  function openNew() { setEditing({ emails: "", label: "" }); }
  function openEdit(row) {
    setEditing({
      id: row.id,
      emails: (row.emails || []).join(", "),
      label: row.label || "",
    });
  }
  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      await api.post("/credit-apply/email-aliases", {
        id: editing.id, emails: editing.emails, label: editing.label || null,
      });
      toast.success("Email alias saved");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }
  async function remove(row) {
    if (!window.confirm(`Remove the alias group ${row.label || row.emails[0]}?`)) return;
    try {
      await api.delete(`/credit-apply/email-aliases/${row.id}`);
      toast.success("Alias group removed");
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="mt-8 rounded-lg border border-zinc-200 p-4"
      data-testid="client-email-aliases">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <Users className="h-3.5 w-3.5" /> Client email aliases
          </div>
          <p className="text-xs text-zinc-600 mt-2 max-w-3xl">
            Some clients register multiple email addresses in Acuity — a
            work + personal account, or Hotmail + Gmail. Class-pack
            certificates end up under whichever email was used at purchase
            time, but bookings might be made under either. Group them here
            and the auto-apply will treat them as one client (symmetric
            — order doesn&apos;t matter).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={openNew}
          data-testid="email-alias-add">
          <Plus className="h-3 w-3 mr-1" /> Add alias group
        </Button>
      </div>

      <div className="mt-3 rounded-md border border-zinc-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">Emails (all equivalent)</th>
              <th className="w-20 px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="px-3 py-3 text-center text-zinc-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={3} className="px-3 py-3 text-center text-zinc-500">
                No email alias groups yet.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-zinc-100"
                data-testid={`email-alias-row-${r.id}`}>
                <td className="px-3 py-1.5 font-medium">{r.label || "—"}</td>
                <td className="px-3 py-1.5">
                  {(r.emails || []).map((e) => (
                    <div key={e} className="font-mono text-2xs">{e}</div>
                  ))}
                </td>
                <td className="px-3 py-1.5 text-right space-x-1">
                  <Button variant="outline" size="sm" onClick={() => openEdit(r)}
                    data-testid={`email-alias-edit-${r.id}`}>Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => remove(r)}
                    data-testid={`email-alias-delete-${r.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="mt-4 rounded-md border border-zinc-300 bg-zinc-50 p-3 space-y-3"
          data-testid="email-alias-editor">
          <div>
            <Label className="text-zinc-700 text-xs">Client name (optional)</Label>
            <Input value={editing.label}
              onChange={(e) => setEditing({ ...editing, label: e.target.value })}
              placeholder="e.g. Jenelle Templeton" className="mt-1"
              data-testid="email-alias-label-input" />
          </div>
          <div>
            <Label className="text-zinc-700 text-xs">Emails</Label>
            <Input value={editing.emails}
              onChange={(e) => setEditing({ ...editing, emails: e.target.value })}
              placeholder="tempysmum@hotmail.com, tempysmum@gmail.com"
              className="mt-1" data-testid="email-alias-emails-input" />
            <p className="mt-1 text-2xs text-zinc-500">
              Comma-separated. Every email in the group can draw from any
              other email&apos;s certificates.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}
              data-testid="email-alias-save">
              {saving ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function CreditShareLinksBlock() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, holder_email, beneficiary_emails, note}

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/credit-apply/share-links");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error("Could not load share links", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function openNew() {
    setEditing({ holder_email: "", beneficiary_emails: "", note: "" });
  }
  function openEdit(row) {
    setEditing({
      id: row.id,
      holder_email: row.holder_email,
      beneficiary_emails: (row.beneficiary_emails || []).join(", "),
      note: row.note || "",
    });
  }
  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      await api.post("/credit-apply/share-links", {
        id: editing.id,
        holder_email: editing.holder_email.trim(),
        beneficiary_emails: editing.beneficiary_emails,
        note: editing.note || null,
      });
      toast.success("Share link saved");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }
  async function remove(row) {
    if (!window.confirm(`Remove the share link from ${row.holder_email}?`)) return;
    try {
      await api.delete(`/credit-apply/share-links/${row.id}`);
      toast.success("Share link removed");
      refresh();
    } catch (e) {
      toast.error("Delete failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  return (
    <div className="mt-8 rounded-lg border border-zinc-200 p-4"
      data-testid="credit-share-links">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <Users className="h-3.5 w-3.5" /> Shared credit accounts
          </div>
          <p className="text-xs text-zinc-600 mt-2 max-w-3xl">
            Some clients buy a class-credit package and let a family member
            book under a different email. Link the holder&apos;s email to
            the beneficiary emails here so the auto-apply job knows to draw
            from that shared certificate pool.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={openNew}
          data-testid="credit-share-add">
          <Plus className="h-3 w-3 mr-1" /> Add link
        </Button>
      </div>

      <div className="mt-3 rounded-md border border-zinc-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2">Holder email</th>
              <th className="text-left px-3 py-2">Shared with</th>
              <th className="text-left px-3 py-2">Note</th>
              <th className="w-20 px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-3 py-3 text-center text-zinc-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-3 text-center text-zinc-500">
                No shared credit accounts yet.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-zinc-100"
                data-testid={`credit-share-row-${r.id}`}>
                <td className="px-3 py-1.5 font-mono text-2xs">{r.holder_email}</td>
                <td className="px-3 py-1.5">
                  {(r.beneficiary_emails || []).map((b) => (
                    <div key={b} className="font-mono text-2xs">{b}</div>
                  ))}
                </td>
                <td className="px-3 py-1.5 text-zinc-600">{r.note || "—"}</td>
                <td className="px-3 py-1.5 text-right space-x-1">
                  <Button variant="outline" size="sm" onClick={() => openEdit(r)}
                    data-testid={`credit-share-edit-${r.id}`}>Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => remove(r)}
                    data-testid={`credit-share-delete-${r.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="mt-4 rounded-md border border-zinc-300 bg-zinc-50 p-3 space-y-3"
          data-testid="credit-share-editor">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-700 text-xs">Holder email</Label>
              <Input value={editing.holder_email}
                onChange={(e) => setEditing({ ...editing, holder_email: e.target.value })}
                placeholder="parent@example.com" className="mt-1"
                data-testid="credit-share-holder-input" />
            </div>
            <div>
              <Label className="text-zinc-700 text-xs">Note (optional)</Label>
              <Input value={editing.note}
                onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                placeholder="e.g. Alford family pack" className="mt-1"
                data-testid="credit-share-note-input" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-700 text-xs">Beneficiary emails</Label>
            <Input value={editing.beneficiary_emails}
              onChange={(e) => setEditing({ ...editing, beneficiary_emails: e.target.value })}
              placeholder="child1@example.com, child2@example.com"
              className="mt-1"
              data-testid="credit-share-beneficiaries-input" />
            <p className="mt-1 text-2xs text-zinc-500">
              Comma-separated. These accounts&apos; appointments will be able to draw
              from the holder&apos;s certificates.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}
              data-testid="credit-share-save">
              {saving ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreditApplyDiagnoseBlock() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [applyBusy, setApplyBusy] = useState({});

  async function runDiagnose() {
    if (!email.trim()) {
      toast.error("Enter the customer's email");
      return;
    }
    setBusy(true);
    setData(null);
    try {
      const r = await api.get("/credit-apply/debug", {
        params: { email: email.trim() },
        timeout: 90000,
      });
      setData(r.data);
      if (r.data?.error) {
        toast.error("Diagnostic returned an error", { description: r.data.error });
      }
    } catch (e) {
      toast.error("Diagnostic failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusy(false);
    }
  }

  async function manualApply(appointment_id, certificate_code) {
    const key = `${appointment_id}:${certificate_code}`;
    setApplyBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await api.post("/credit-apply/manual-apply", null, {
        params: { appointment_id, certificate_code, push_to_acuity: true },
        timeout: 60000,
      });
      if (r.data?.ok) {
        toast.success(`Credit ${certificate_code} applied to appointment ${appointment_id}`, {
          description: "Run diagnostic again to refresh the view if you want.",
        });
        // Reflect the success in-place without re-querying Acuity (the user
        // can hit "Run diagnostic" again manually when they're ready).
        setData((d) => {
          if (!d?.customer_appts) return d;
          return {
            ...d,
            customer_appts: d.customer_appts.map((a) =>
              String(a.id) === String(appointment_id)
                ? { ...a, is_outstanding: false, _applied_code: certificate_code }
                : a,
            ),
          };
        });
      } else {
        toast.error("Acuity refused the credit", {
          description: r.data?.error || `HTTP ${r.data?.status_code}`,
        });
      }
    } catch (e) {
      toast.error("Manual apply failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setApplyBusy((b) => ({ ...b, [key]: false }));
    }
  }

  const apptCertOptions = (data?.customer_certs || []).concat(data?.candidate_other_certs || []);
  // Per-appointment "paste a known cert code" state.
  const [manualCodeByAppt, setManualCodeByAppt] = useState({});

  return (
    <div className="mt-6 border-t border-zinc-200 pt-5">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
        Diagnose / Manual apply
      </div>
      <p className="text-xs text-zinc-600 mb-3">
        Run a per-customer check to see why a credit isn&apos;t being auto-applied. Also surfaces certificates
        registered under a different email (shared memberships).
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-xs">Customer email</Label>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runDiagnose(); }}
            placeholder="jacque_haslamryan@yahoo.com.au"
            data-testid="credit-apply-debug-email"
          />
        </div>
        <Button onClick={runDiagnose} disabled={busy} data-testid="credit-apply-debug-run">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Looking…" : "Run diagnostic"}
        </Button>
      </div>

      {data && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Stat label="Appts in window" value={data.customer_appts_count ?? 0} />
            <Stat label="Certs (this email)" value={data.customer_certs_count ?? 0} />
            <Stat
              label="Certs (other email)"
              value={data.candidate_other_certs_count ?? 0}
              warn={data.customer_certs_count === 0 && (data.candidate_other_certs_count || 0) > 0}
            />
            <Stat label="Certs in account" value={data.certs_total ?? 0} />
          </div>

          {(data.customer_appts || []).map((a) => (
            <div key={a.id} className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-sm font-medium text-zinc-900">
                  {a.datetime?.replace("T", " ").slice(0, 16) || "—"} · {a.type || "—"}
                </div>
                <div className="text-2xs font-mono-num text-zinc-500">
                  Appt {a.id}
                </div>
              </div>
              <div className="mt-1 text-2xs text-zinc-600">
                amountPaid: <span className="font-mono-num">{a.amountPaid}</span> ·
                calculated: <span className="font-mono-num">{a.calculated_price}</span> ·
                outstanding: <span className={a.is_outstanding ? "text-amber-700 font-semibold" : "text-emerald-700"}>{String(a.is_outstanding)}</span>
              </div>

              {a.is_outstanding && (
                <div className="mt-3 border-t border-dashed border-zinc-300 pt-2">
                  <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">
                    Already know the cert code? Apply it directly
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={manualCodeByAppt[a.id] || ""}
                      onChange={(e) =>
                        setManualCodeByAppt((m) => ({
                          ...m,
                          [a.id]: e.target.value.toUpperCase(),
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && manualCodeByAppt[a.id]) {
                          manualApply(a.id, manualCodeByAppt[a.id].trim());
                        }
                      }}
                      placeholder="e.g. 75D081F3"
                      className="font-mono-num h-8 text-xs"
                      data-testid={`credit-apply-manual-code-${a.id}`}
                    />
                    <Button
                      size="sm"
                      className="h-8 px-3 text-xs"
                      onClick={() => {
                        const code = (manualCodeByAppt[a.id] || "").trim();
                        if (!code) {
                          toast.error("Paste the certificate code first");
                          return;
                        }
                        manualApply(a.id, code);
                      }}
                      disabled={!!applyBusy[`${a.id}:${(manualCodeByAppt[a.id] || "").trim()}`]}
                      data-testid={`credit-apply-manual-code-apply-${a.id}`}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              )}

              {a.is_outstanding && apptCertOptions.length > 0 && (
                <div className="mt-2">
                  <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">
                    Apply a certificate manually
                  </div>
                  <div className="space-y-1">
                    {apptCertOptions.map((c) => {
                      const code = c.certificate || c.code;
                      const rcKey = String(a.appointmentTypeID);
                      const rc = c.remainingCounts || {};
                      const allowedTypes = c.appointmentTypeIDs || [];
                      const remainingForType = rc[rcKey] ?? rc[Number(rcKey)];
                      const allowed = allowedTypes
                        .map(String)
                        .includes(String(a.appointmentTypeID));
                      const eligible = (remainingForType ?? 0) > 0 ||
                        (allowed && (c.remainingValue || 0) > 0);
                      const key = `${a.id}:${code}`;
                      return (
                        <div
                          key={key}
                          className="flex items-center gap-2 text-2xs bg-white border border-zinc-200 rounded px-2 py-1.5"
                        >
                          <span className="font-mono-num font-semibold text-zinc-900">{code}</span>
                          <span className="text-zinc-600">{c.name || "—"}</span>
                          {c.match_reason && (
                            <span className="text-amber-700 text-2xs">({c.match_reason})</span>
                          )}
                          <span className="text-zinc-500 ml-auto">
                            rem for type {rcKey}: <span className="font-mono-num">{remainingForType ?? "—"}</span>
                          </span>
                          <Button
                            size="sm"
                            variant={eligible ? "default" : "outline"}
                            className="h-6 px-2 text-2xs"
                            onClick={() => manualApply(a.id, code)}
                            disabled={!!applyBusy[key]}
                            data-testid={`credit-apply-manual-${a.id}-${code}`}
                          >
                            {applyBusy[key] ? "Applying…" : "Apply"}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}

          {(data.candidate_other_certs || []).length > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded p-3"
              data-testid="candidate-other-certs">
              <div className="text-xs font-semibold text-amber-900">
                💡 Found {data.candidate_other_certs_count} cert(s) with credits remaining under a matching name but a different email
              </div>
              <p className="text-2xs text-amber-800 mt-1">
                These certs have both matching first AND last name to this
                client&apos;s bookings, and still have credits available. To
                make the cron auto-apply them, add the email shown to this
                client&apos;s Email Alias group.
              </p>
              <div className="mt-3 space-y-1">
                {data.candidate_other_certs.map((c) => {
                  const rc = c.remainingCounts || {};
                  const remainingTotal = Object.values(rc).reduce((s, v) => s + (Number(v) || 0), 0)
                    + (c.remainingValue ? 1 : 0);
                  return (
                    <div key={c.id || c.certificate}
                      className="grid grid-cols-[130px_1fr_auto] gap-2 items-center rounded p-1.5 text-2xs bg-emerald-50 border border-emerald-200"
                      data-testid={`candidate-cert-${c.certificate}`}>
                      <span className="font-mono text-2xs">{c.certificate}</span>
                      <span>
                        <div className="font-semibold text-zinc-800">
                          registered under: <span className="font-mono">{c.true_email || c.registered_email || "(no email)"}</span>
                        </div>
                        <div className="text-zinc-600">
                          order customer: {c.order_customer || "—"} · pack: {c.name || "—"}
                        </div>
                      </span>
                      <span className="text-right text-emerald-800 font-semibold">
                        {remainingTotal} left
                      </span>
                    </div>
                  );
                })}
              </div>
              <details className="mt-3">
                <summary className="cursor-pointer text-2xs text-amber-900">Raw JSON</summary>
                <pre className="mt-2 text-2xs bg-white p-2 rounded overflow-auto max-h-64">
{JSON.stringify(data.candidate_other_certs, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div
        className={
          "mt-1 text-2xl font-mono-num font-medium " +
          (warn ? "text-amber-700" : "text-zinc-950")
        }
      >
        {value ?? "—"}
      </div>
    </div>
  );
}
